import { useCallback, useEffect, useState } from 'react';

import { getSnapshotComparisonApplications } from '../../../api/snapshotChangeDetection';
import { CENTER, DEFAULT_FILTERS, DEFAULT_PAGE_SIZE } from '../constants';
import { SnapshotComparisonApplication, SnapshotComparisonFilters, TriStateFilter } from '../types';
import { parseTriStateFilter } from '../utils';

const useSnapshotComparisonList = () => {
  const [filters, setFilters] = useState<SnapshotComparisonFilters>(DEFAULT_FILTERS);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);
  const [rows, setRows] = useState<SnapshotComparisonApplication[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchApplications = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await getSnapshotComparisonApplications({
        center: CENTER,
        application_number: filters.applicationNumber.trim() || undefined,
        qc_status: parseTriStateFilter(filters.qcStatus),
        change_detection_status: parseTriStateFilter(filters.changeDetectionStatus),
        page,
        page_size: pageSize
      });

      if (response?.data?.status === 200) {
        setRows(response.data.body?.results ?? []);
        setTotal(response.data.body?.total ?? 0);
        return;
      }

      setRows([]);
      setTotal(0);
      setError('Unable to load snapshot comparison applications.');
    } catch {
      setRows([]);
      setTotal(0);
      setError('Unable to load snapshot comparison applications.');
    } finally {
      setLoading(false);
    }
  }, [filters, page, pageSize]);

  useEffect(() => {
    fetchApplications();
  }, [fetchApplications]);

  const setFilter = <K extends keyof SnapshotComparisonFilters>(
    key: K,
    value: SnapshotComparisonFilters[K]
  ) => {
    setFilters(previous => ({ ...previous, [key]: value }));
    setPage(0);
  };

  const setQcStatus = (value: TriStateFilter) => setFilter('qcStatus', value);

  const setChangeDetectionStatus = (value: TriStateFilter) =>
    setFilter('changeDetectionStatus', value);

  const setApplicationNumber = (value: string) => setFilter('applicationNumber', value);

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS);
    setPage(0);
  };

  const changePage = (nextPage: number) => setPage(nextPage);

  const changePageSize = (nextPageSize: number) => {
    setPageSize(nextPageSize);
    setPage(0);
  };

  return {
    rows,
    total,
    loading,
    error,
    page,
    pageSize,
    filters,
    setQcStatus,
    setChangeDetectionStatus,
    setApplicationNumber,
    clearFilters,
    changePage,
    changePageSize
  };
};

export default useSnapshotComparisonList;
